import { Typography } from "@hellocarbo/lasagne";
import { use } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { PublicReviewBanner, ReviewBanner } from "@/components/Banner";
import { NavigationMenuLinks } from "@/components/NavigationMenu";
import ComparisonDatabase from "@/components/Review/ComparisonDatabase";
import SectorComparison from "@/components/Review/SectorComparison";
import { UserContext } from "@/contexts";
import { CustomUIMatch } from "@/hooks/useBreadcrumbs";
import { formatEndDate } from "@/utils/formatDate";

import { useReviewContext } from "./Review";

export const handle = {
  activeMenuLink: () => NavigationMenuLinks.restitution,
  breadcrumb: ({ reviewId, slug }) => {
    return [
      {
        href: reviewId
          ? `/reviews/${reviewId}/comparaison`
          : `/reviews/${slug}/public/comparaison`,
        label: "review:comparison:sectoralComparison",
      },
    ];
  },
} as CustomUIMatch["handle"];

function ReviewComparison() {
  const { reviewData, industryData } = useReviewContext();
  const { t, i18n } = useTranslation(["common", "formatter", "review"]);
  const { slug, reviewId } = useParams();

  const isPublicReview = !reviewId && slug;

  if (!reviewData || !industryData) {
    return (
      <div className="grid place-items-center">
        <p>{t("common:loading")}</p>
      </div>
    );
  }

  const {
    company_name: companyName,
    carbon,
    employees,
    turnover,
    period_start: periodStart,
    period_end: periodEnd,
    hide_economic_intensity,
  } = reviewData;

  const { title_en, title_fr, comparison } = industryData;
  const { user } = use(UserContext);

  return (
    <>
      <Typography step="3" as="h1" weight="bold" className="mb-3xs">
        {t("review:comparison.sectoralComparison")}
      </Typography>
      <Typography className="text-grayscale-dark">
        {t("formatter:date", { value: new Date(periodStart) })}
        &nbsp;-&nbsp;
        {t("formatter:date", {
          value: formatEndDate(new Date(periodEnd)),
        })}
      </Typography>
      <div className="my-md gap-y-sm lg:gap-md grid xl:grid-cols-[1fr_3fr]">
        <div className="xl:order-last">
          <SectorComparison
            carbon={carbon}
            employees={employees}
            turnover={turnover}
            comparison={comparison}
            hideTurnover={hide_economic_intensity}
          />
        </div>
        <ComparisonDatabase
          title={i18n.language === "en" ? title_en : title_fr}
          comparison={comparison}
          hideTurnover={hide_economic_intensity}
        />
      </div>

      {!isPublicReview && user && (
        <ReviewBanner linkUrl={`/reviews/${reviewId}/feedback`} />
      )}
      {isPublicReview && !user && (
        <PublicReviewBanner companyName={companyName} />
      )}
    </>
  );
}

export default ReviewComparison;
